<template>
	<div>
		<DocumentCropper
			v-if="cropping"
			:image-data="rawFrameForCrop"
			:initial-corners="initialCornersPercent"
			@cropped="handleCropped"
			@cancel="handleCropCancel"
		/>

		<Teleport to="body">
			<div class="camera-fullscreen" v-show="displayCamera" ref="photo">
				<div class="video-stage">
					<video ref="video" id="video" playsinline muted></video>
					<canvas ref="overlay" id="overlay"></canvas>
				</div>
				<canvas ref="canvas" id="canvas"></canvas>
				<canvas ref="detect" id="detect"></canvas>

				<div class="camera-hint" v-if="pages.length">
					Page {{ pages.length + 1 }}
				</div>

				<button class="camera-close" @click.prevent="closeCamera()">
					<i class="fas fa-times"></i>
				</button>

				<div class="photo-button" @click.prevent="extract()">
					<div class="circle"></div>
					<div class="ring"></div>
				</div>
			</div>
		</Teleport>

		<Teleport to="body">
			<div class="gallery-modal" v-if="step === 'gallery'">
				<div class="gallery-header">
					<h2>Justificatif photo</h2>
					<button class="icon-btn" @click="cancelAll()" title="Annuler">
						<i class="fas fa-times"></i>
					</button>
				</div>

				<div class="gallery-hint">
					<i class="fas fa-info-circle me-1"></i>
					Votre document comporte plusieurs pages&nbsp;? Ajoutez-en une autre, elles seront réunies en un seul PDF.
				</div>

				<div class="gallery-grid">
					<div class="gallery-item" v-for="(page, index) in pages" :key="index">
						<img :src="page" alt="">
						<span class="gallery-page-number">{{ index + 1 }}</span>
						<button class="gallery-remove" @click="removePage(index)" title="Supprimer cette page">
							<i class="fas fa-trash-alt"></i>
						</button>
					</div>
					<button class="gallery-add" @click="startCamera()">
						<i class="fas fa-camera fa-2x mb-2"></i>
						<span>Ajouter une page</span>
					</button>
				</div>

				<div class="gallery-footer">
					<button class="btn btn-light" @click="cancelAll()">
						Annuler
					</button>
					<button class="btn btn-primary" :disabled="!pages.length || building" @click="finish()">
						<span v-if="building"><i class="fas fa-spinner fa-spin me-2"></i>Génération du PDF…</span>
						<span v-else><i class="fas fa-check me-2"></i>Valider ({{ pages.length }} page{{ pages.length > 1 ? 's' : '' }})</span>
					</button>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
import DocumentCropper from "../ExpensesScan/DocumentCropper.vue";
import jscanify from "jscanify/src/jscanify.js";
import jsPDF from "jspdf";

export default {
	components: {DocumentCropper},
	emits: ['done', 'cancel'],
	data() {
		return {
			step: 'camera',
			pages: [],
			displayCamera: false,
			cropping: false,
			rawFrameForCrop: null,
			initialCornersPercent: null,
			building: false,
			scanner: null,
			canvasCtx: null,
			detectCtx: null,
			overlayCtx: null,
			rafId: null,
			detecting: false,
			autoCapture: true,
			stableFrames: 0,
			capturing: false,
			lastCorners: null,
			STABLE_THRESHOLD: 12,
			STABLE_TOLERANCE: 6,
		}
	},
	mounted() {
		this.scanner = new jscanify();
		this.canvasCtx = this.$refs.canvas.getContext("2d", {willReadFrequently: true});
		this.detectCtx = this.$refs.detect.getContext("2d", {willReadFrequently: true});
		this.overlayCtx = this.$refs.overlay.getContext("2d");
		this.startCamera();
	},
	beforeUnmount() {
		this.stopCamera();
	},
	methods: {
		startCamera() {
			this.step = 'camera';
			this.stableFrames = 0;
			this.capturing = false;
			this.lastCorners = null;
			this.displayCamera = true;
			document.body.classList.add('camera-open');
			this.enterNativeFullscreen();

			navigator.mediaDevices.getUserMedia({
				audio: false,
				video: {
					facingMode: "environment",
					width: {ideal: 1920},
					height: {ideal: 1080},
				}
			}).then((stream) => {
				this.$refs.video.srcObject = stream;
				this.$refs.video.onloadedmetadata = () => {
					this.$refs.video.play();

					const vw = this.$refs.video.videoWidth;
					const vh = this.$refs.video.videoHeight;

					this.$refs.canvas.width = vw;
					this.$refs.canvas.height = vh;

					const dw = 480;
					const dh = Math.round(dw * vh / vw);
					this.$refs.detect.width = dw;
					this.$refs.detect.height = dh;

					this.$refs.overlay.width = dw;
					this.$refs.overlay.height = dh;

					this.detectLoop();
				};
			});
		},
		detectLoop() {
			this.rafId = requestAnimationFrame(this.detectLoop);
			if (this.detecting) return;
			this.detecting = true;
			try {
				const d = this.$refs.detect;
				this.detectCtx.drawImage(this.$refs.video, 0, 0, d.width, d.height);
				const img = cv.imread(d);
				const maxContour = this.scanner.findPaperContour(img);
				this.overlayCtx.clearRect(0, 0, d.width, d.height);
				let cornersOk = false;
				let c = null;
				if (maxContour) {
					c = this.scanner.getCornerPoints(maxContour, img);
					if (c.topLeftCorner && c.topRightCorner && c.bottomLeftCorner && c.bottomRightCorner) {
						cornersOk = this.isValidQuad(c, d.width, d.height);
					}
				}
				img.delete();

				if (cornersOk) {
					const steady = this.cornersSteady(c);
					this.lastCorners = c;
					if (steady) this.stableFrames++;
					else this.stableFrames = 1;

					const locked = this.stableFrames >= this.STABLE_THRESHOLD;
					this.drawQuad(c, locked ? "#22c55e" : "orange");

					if (locked && this.autoCapture && !this.capturing) {
						this.capturing = true;
						this.extract();
					}
				} else {
					this.stableFrames = 0;
					this.lastCorners = null;
				}
			} catch (e) {
				/* transient frame error — keep looping */
			} finally {
				this.detecting = false;
			}
		},
		drawQuad(c, color) {
			const ctx = this.overlayCtx;
			ctx.strokeStyle = color;
			ctx.lineWidth = 3;
			ctx.beginPath();
			ctx.moveTo(c.topLeftCorner.x, c.topLeftCorner.y);
			ctx.lineTo(c.topRightCorner.x, c.topRightCorner.y);
			ctx.lineTo(c.bottomRightCorner.x, c.bottomRightCorner.y);
			ctx.lineTo(c.bottomLeftCorner.x, c.bottomLeftCorner.y);
			ctx.closePath();
			ctx.stroke();
		},
		isValidQuad(c, w, h) {
			const {topLeftCorner: tl, topRightCorner: tr, bottomLeftCorner: bl, bottomRightCorner: br} = c;
			const quadW = Math.max(tr.x - tl.x, br.x - bl.x);
			const quadH = Math.max(bl.y - tl.y, br.y - tr.y);
			if (quadW < w * 0.35 || quadH < h * 0.35) return false;
			return true;
		},
		cornersSteady(c) {
			if (!this.lastCorners) return false;
			const keys = ['topLeftCorner', 'topRightCorner', 'bottomLeftCorner', 'bottomRightCorner'];
			for (const k of keys) {
				const dx = c[k].x - this.lastCorners[k].x;
				const dy = c[k].y - this.lastCorners[k].y;
				if (Math.hypot(dx, dy) > this.STABLE_TOLERANCE) return false;
			}
			return true;
		},
		async extract() {
			this.stopDetect();

			const cw = this.$refs.canvas.width;
			const ch = this.$refs.canvas.height;
			this.canvasCtx.drawImage(this.$refs.video, 0, 0);

			const img = cv.imread(this.$refs.canvas);
			let corners = null;
			try {
				const maxContour = this.scanner.findPaperContour(img);
				if (maxContour) {
					const c = this.scanner.getCornerPoints(maxContour, img);
					if (c.topLeftCorner && c.topRightCorner && c.bottomLeftCorner && c.bottomRightCorner) {
						corners = c;
					}
				}
			} catch (e) {
				corners = null;
			}
			img.delete();

			this.initialCornersPercent = corners ? {
				topLeft: {x: corners.topLeftCorner.x / cw * 100, y: corners.topLeftCorner.y / ch * 100},
				topRight: {x: corners.topRightCorner.x / cw * 100, y: corners.topRightCorner.y / ch * 100},
				bottomRight: {x: corners.bottomRightCorner.x / cw * 100, y: corners.bottomRightCorner.y / ch * 100},
				bottomLeft: {x: corners.bottomLeftCorner.x / cw * 100, y: corners.bottomLeftCorner.y / ch * 100},
			} : null;

			this.rawFrameForCrop = this.$refs.canvas.toDataURL("image/jpeg", 0.92);
			this.stopCamera();
			this.cropping = true;
		},
		handleCropped(dataUrl) {
			this.cropping = false;
			this.pages.push(dataUrl);
			this.step = 'gallery';
		},
		handleCropCancel() {
			this.cropping = false;
			this.rawFrameForCrop = null;
			this.startCamera();
		},
		removePage(index) {
			this.pages.splice(index, 1);
		},
		enterNativeFullscreen() {
			const el = this.$refs.photo;
			const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
			if (req) {
				try { req.call(el); } catch (e) { /* unsupported/denied — overlay still covers screen */ }
			}
		},
		exitNativeFullscreen() {
			const isFs = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
			if (!isFs) return;
			const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
			if (exit) {
				try { exit.call(document); } catch (e) { /* ignore */ }
			}
		},
		stopDetect() {
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
			this.detecting = false;
		},
		stopCamera() {
			this.exitNativeFullscreen();
			this.stopDetect();
			const stream = this.$refs.video && this.$refs.video.srcObject;
			if (stream) {
				stream.getTracks().forEach(t => t.stop());
				this.$refs.video.srcObject = null;
			}
			this.displayCamera = false;
			document.body.classList.remove('camera-open');
		},
		closeCamera() {
			this.stopCamera();
			if (this.pages.length) {
				this.step = 'gallery';
			} else {
				this.$emit('cancel');
			}
		},
		cancelAll() {
			this.stopCamera();
			this.$emit('cancel');
		},
		getImageDimensions(dataUrl) {
			return new Promise((resolve) => {
				const img = new Image();
				img.onload = () => resolve({width: img.width, height: img.height});
				img.src = dataUrl;
			});
		},
		async finish() {
			if (!this.pages.length || this.building) return;
			this.building = true;
			try {
				const pdf = new jsPDF({unit: 'mm', format: 'a4'});
				const margin = 8;
				for (let i = 0; i < this.pages.length; i++) {
					if (i > 0) pdf.addPage();
					const dims = await this.getImageDimensions(this.pages[i]);
					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const maxW = pageWidth - margin * 2;
					const maxH = pageHeight - margin * 2;
					const ratio = Math.min(maxW / dims.width, maxH / dims.height);
					const w = dims.width * ratio;
					const h = dims.height * ratio;
					const x = (pageWidth - w) / 2;
					const y = (pageHeight - h) / 2;
					pdf.addImage(this.pages[i], 'JPEG', x, y, w, h);
				}
				const blob = pdf.output('blob');
				const file = new File([blob], `justificatif-${Date.now()}.pdf`, {type: 'application/pdf'});
				this.$emit('done', file);
			} finally {
				this.building = false;
			}
		},
	}
}
</script>

<style scoped>
.camera-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 2000;
	width: 100vw;
	height: 100vh;
	height: 100dvh;
	background-color: #000;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.video-stage {
	position: relative;
	max-width: 100%;
	max-height: 100%;
	display: flex;
}

.camera-fullscreen #video {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
	display: block;
}

.camera-fullscreen #overlay {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
}

#canvas, #detect {
	display: none;
}

.camera-hint {
	position: absolute;
	top: env(safe-area-inset-top, 16px);
	left: 50%;
	transform: translateX(-50%);
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	padding: 6px 16px;
	border-radius: 999px;
	font-size: 0.9rem;
	z-index: 20;
}

.camera-close {
	position: absolute;
	top: env(safe-area-inset-top, 16px);
	right: 16px;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.5);
	color: #fff;
	font-size: 20px;
	z-index: 20;
	cursor: pointer;
}

.photo-button {
	width: 80px;
	height: 80px;
	bottom: 20px;
	left: 50%;
	margin-left: -40px;
	position: absolute;
	z-index: 10;
	cursor: pointer;
}

.circle {
	position: absolute;
	top: 12%;
	left: 12%;
	bottom: 12%;
	right: 12%;
	border-radius: 100%;
	background-color: #ffffff;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.ring {
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	border-radius: 100%;
	border: 4px solid #ffffff;
	opacity: 0.9;
	transition: all 0.2s ease;
}

.photo-button:hover .circle {
	opacity: 0.3;
}

.photo-button:active .ring {
	transform: scale(0.95);
	opacity: 1;
}

.photo-button:active .circle {
	opacity: 0.5;
}

/* Gallery */
.gallery-modal {
	position: fixed;
	inset: 0;
	z-index: 2050;
	background-color: #fff;
	display: flex;
	flex-direction: column;
}

.gallery-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1rem 1.25rem;
	border-bottom: 1px solid #eee;
}

.gallery-header h2 {
	font-size: 1.15rem;
	margin: 0;
	font-weight: 600;
}

.gallery-header .icon-btn {
	background: none;
	border: none;
	font-size: 1.25rem;
	color: #6b7280;
	cursor: pointer;
	padding: 0.25rem 0.5rem;
}

.gallery-hint {
	background-color: #e7f3ff;
	color: #1868c1;
	padding: 0.65rem 1.25rem;
	font-size: 0.85rem;
}

.gallery-grid {
	flex: 1;
	overflow-y: auto;
	padding: 1.25rem;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
	gap: 1rem;
	align-content: start;
}

.gallery-item {
	position: relative;
	aspect-ratio: 3/4;
	border-radius: 10px;
	overflow: hidden;
	background-color: #f3f4f6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.gallery-item img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.gallery-page-number {
	position: absolute;
	left: 6px;
	bottom: 6px;
	background: rgba(0, 0, 0, 0.6);
	color: #fff;
	font-size: 0.75rem;
	padding: 1px 8px;
	border-radius: 999px;
}

.gallery-remove {
	position: absolute;
	top: 6px;
	right: 6px;
	width: 28px;
	height: 28px;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.55);
	color: #fff;
	cursor: pointer;
}

.gallery-add {
	aspect-ratio: 3/4;
	border-radius: 10px;
	border: 2px dashed #2080f0;
	background: #f0f7ff;
	color: #2080f0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;
	cursor: pointer;
}

.gallery-add:hover {
	background: #e1f0ff;
}

.gallery-footer {
	display: flex;
	gap: 0.75rem;
	padding: 1rem 1.25rem;
	padding-bottom: max(1rem, env(safe-area-inset-bottom));
	border-top: 1px solid #eee;
}

.gallery-footer .btn {
	flex: 1;
	padding: 0.65rem 1rem;
}

.gallery-footer .btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
</style>

<style>
body.camera-open {
	overflow: hidden;
}
</style>
